import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaLinkedin, FaTwitter, FaGithub } from 'react-icons/fa';
import { Lightbulb, Eye, Target, ArrowRight, ShieldCheck } from 'lucide-react';

/* ----------------------------------------------------------------------------
 * About page — editorial layout, single signal-green accent, hairline borders,
 * serif headings. Matches Home.js so marketing and product feel like one system.
 * -------------------------------------------------------------------------- */

const GREEN = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

const team = [
  {
    name: 'Ayush Jain',
    role: 'Founder',
    image: '/ayush.jpg',
    bio: 'Building AutoHire because hiring should be based on real work by real people. Early-stage, hands-on, and talking to every design partner directly.',
    social: {
      linkedin: 'https://www.linkedin.com/in/ayush-jain-19191a1a6/',
      twitter: '#',
      github: '#',
    },
  },
];

const VALUES = [
  {
    icon: Lightbulb,
    title: 'Real work over signals',
    desc: 'We judge people on work that resembles the job — not keywords, trivia, or pedigree.',
  },
  {
    icon: Eye,
    title: 'Explainable by default',
    desc: 'Every score is computed in code and shows its reasoning. If we can\'t explain it, we don\'t ship it.',
  },
  {
    icon: Target,
    title: 'Honest while early',
    desc: 'No fake logos, no inflated metrics. We\'d rather earn trust than borrow it.',
  },
];

const PRINCIPLES = [
  { value: 'In code', label: 'Scores computed, not asserted by a model' },
  { value: 'Verified', label: 'A real person behind every result' },
  { value: 'Logged', label: 'Explainable and exportable for audit' },
];

function About() {
  return (
    <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center lg:px-8 lg:py-28">
          <motion.div {...reveal} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
            Our mission
          </motion.div>
          <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
            className="mt-6 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
            About AutoHire
          </motion.h1>
          <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            We believe hiring should be based on real work by real people — provable, explainable, and fair.
          </motion.p>
        </div>
      </section>

      {/* ===== PRINCIPLES STRIP ===== */}
      <section className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            {PRINCIPLES.map((s, i) => (
              <motion.div key={s.label} {...reveal} transition={{ duration: 0.5, delay: i * 0.08 }} className="text-center">
                <div className="font-serif text-4xl font-semibold sm:text-5xl" style={{ color: GREEN }}>{s.value}</div>
                <div className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STORY ===== */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="lg:col-span-4">
            <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>Our story</div>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Recruitment, rebuilt from the ground up.
            </h2>
          </motion.div>
          <motion.div {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
            className="space-y-6 text-lg leading-relaxed text-gray-600 dark:text-gray-300 lg:col-span-8">
            <p>
              AutoHire was born from a simple observation: with AI everywhere, the interview has stopped telling
              you the truth. Teams burn senior-engineer hours on onsites with fake profiles and copy-paste candidates,
              and detection-and-proctoring is an arms race you lose.
            </p>
            <p>
              So we took a different path: let candidates do real, AI-allowed work, tie it to a verified human, and
              return one score computed in code that you can actually explain. We're early — building it in the open
              with a small group of design partners running their real roles through it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== VALUES ===== */}
      <section className="border-y border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.5 }} className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>What we stand for</div>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">Our values.</h2>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} {...reveal} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-[#F8FAFC] dark:bg-gray-900 p-7 transition-colors hover:border-gray-300 dark:hover:border-gray-700">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: GREEN }}>
                  <v.icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 font-serif text-lg font-semibold text-gray-900 dark:text-white">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TEAM ===== */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <motion.div {...reveal} transition={{ duration: 0.5 }} className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>The people</div>
          <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">Our team.</h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">Meet the people behind AutoHire's success.</p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              {...reveal}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-colors hover:border-gray-300 dark:hover:border-gray-700"
            >
              <div className="aspect-[3/2] bg-gray-100 dark:bg-gray-800">
                <img
                  className="h-full w-full object-cover transition-opacity duration-300 hover:opacity-95"
                  src={member.image}
                  alt={member.name}
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=Team+Member'; }}
                />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold text-gray-900 dark:text-white">{member.name}</h3>
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}>
                  {member.role}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{member.bio}</p>
                <div className="mt-5 flex items-center gap-4">
                  <a
                    href={member.social.linkedin}
                    className="text-gray-400 transition-colors hover:text-gray-900 dark:hover:text-white"
                    target="_blank" rel="noopener noreferrer"
                    aria-label={`${member.name} on LinkedIn`}
                  >
                    <FaLinkedin className="h-5 w-5" />
                  </a>
                  <a
                    href={member.social.twitter}
                    className="text-gray-400 transition-colors hover:text-gray-900 dark:hover:text-white"
                    aria-label={`${member.name} on Twitter`}
                  >
                    <FaTwitter className="h-5 w-5" />
                  </a>
                  <a
                    href={member.social.github}
                    className="text-gray-400 transition-colors hover:text-gray-900 dark:hover:text-white"
                    aria-label={`${member.name} on GitHub`}
                  >
                    <FaGithub className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-[#0F172A]">
        <div className="mx-auto max-w-5xl px-6 py-24 text-center lg:px-8">
          <motion.div {...reveal} transition={{ duration: 0.55 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-gray-300">
              <ShieldCheck size={14} style={{ color: GREEN }} /> Built for recruiters, not to replace them
            </div>
            <h2 className="mt-7 font-serif text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Join us in reshaping how teams hire.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-300">
              See how AutoHire connects talent with opportunity — faster, fairer, and fully transparent.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold text-white" style={{ background: GREEN }}>
                Get a demo <ArrowRight size={17} />
              </Link>
              <Link to="/register" className="inline-flex items-center justify-center rounded-lg border border-white/20 bg-transparent px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10">
                Start free trial
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;
